// react
import { useContext, useMemo } from 'react';

// types
import { Pagination, IfrPlan, UserRoleWbg } from '../../../../@types';
import { userRolesWbg } from '../../../../@types/unions';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useLocation, useSearchParams } from 'react-router-dom';

// context
import { ContextUser } from '../../../../App.context';
import { ContextUserProjectPermissions } from '../../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';

// ui
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { TableHeaders, TableHeadersProps } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// cards
import { CardIfrPlan } from '../../../cards/CardIfrPlan/CardIfrPlan';

export interface TableIfrPlansProps {
	plans: IfrPlan[];
	isLoading?: boolean;
	pagination: Pagination;
}

export const TableIfrPlans = ({ plans, isLoading, pagination }: TableIfrPlansProps) => {
	// hooks
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');

	// context
	const { user } = useContext(ContextUser);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// headers
	const headers: TableHeadersProps['headers'] = [
		{
			GridProps: { lg: 2.5 },
			label: t('name'),
			value: 'name',
		},
		{
			GridProps: { lg: 1.5 },
			label: t('fiscalYear'),
			value: 'fiscalYear',
		},
		{
			GridProps: { lg: 1 },
			label: t('currency'),
			value: 'currency',
		},
		{
			GridProps: { lg: 2 },
			label: t('status'),
			value: 'status',
		},
		{
			GridProps: { lg: 2 },
			label: t('created'),
			value: 'created',
		},
		{
			GridProps: { lg: 2 },
			label: t('modified'),
			value: 'modified',
		},
		{
			GridProps: { lg: 1 },
			label: '',
			value: 'actions',
		},
	];

	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: {
				children: t('empty.plans.content'),
			},
			IconCircleProps: {
				type: 'plan',
			},
			title: { children: t('empty.plans.title') },
		};

		if (
			emptyProps.actions &&
			userProjectPermissions?.payment.create &&
			!location.pathname.includes('entity')
		)
			if (user && !userRolesWbg.includes(user.role as UserRoleWbg)) {
				emptyProps.actions.push({
					children: t('addPlan'),
					onClick: () => {
						searchParams.set('dialog', 'addPlan');
						setSearchParams(searchParams);
					},
					variant: 'contained',
				});
			}

		// redefine empty content if search params exist
		if ([...searchParams].length && !paramDialog) {
			emptyProps.content = {
				children: t('empty.default.content'),
			};
			emptyProps.IconCircleProps = {
				icon: null,
			};
		}

		return emptyProps;
	}, [location, paramDialog, searchParams, setSearchParams, t, user, userProjectPermissions]);

	const content = useMemo(() => {
		if (isLoading || !plans.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{plans.map((plan, i) => {
					return (
						<Grid item key={`payment-${i}`} xs={12}>
							<CardIfrPlan plan={plan} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, isLoading, plans]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					<Grid item xs={12} md={true}>
						<FormSearch InputProps={{ placeholder: t('searchByIdOrName') }} />
					</Grid>
					<Grid item container xs={6} md="auto">
						<BtnFilters filters={[]} />
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={plans.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
